import React, { FC, useCallback, useMemo } from 'react';

import { useFormikContext } from 'formik';

import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';

import useQueryParams from '../../hooks/helper/useQueryParams';

import { TableFilterConfig, TableFilterType } from './types';

import { SearchableSelectType } from '../inputs/searchableSelect';
import FilterDisplay from '../tables/filters/FilterDisplay';

interface FilterChipProps {
    filtersConfig?: TableFilterConfig[];
    handleFilterChange?: (
        item: TableFilterConfig,
        value: string | number | SearchableSelectType[],
    ) => void;
    getSelectedDropdownValue: (
        field: string,
        isMulti: boolean,
    ) => SearchableSelectType | SearchableSelectType[] | undefined;
}

const FilterChip: FC<FilterChipProps> = ({
    filtersConfig,
    handleFilterChange,
    getSelectedDropdownValue,
}) => {
    const filtersState = useQueryParams();
    const { setFieldValue } = useFormikContext();

    const isMultiSelect = useMemo<boolean>(
        () =>
            filtersConfig?.some(
                (item) => item.type === TableFilterType.MULTISELECT,
            )
                ? true
                : false,
        [filtersConfig],
    );
    const displayFilters = useMemo<
        { columnId: string; label: string; value: string }[]
    >(() => {
        let filters: { columnId: string; label: string; value: string }[] = [];
        filtersConfig
            ?.filter((item) => item.showFilterChip !== false)
            .forEach((item) => {
                const paramValue = filtersState[item.field];

                const values =
                    typeof paramValue === 'string'
                        ? paramValue.split(',')
                        : Array.isArray(paramValue)
                        ? paramValue
                        : [];
                const data = values.map((value: string) => ({
                    columnId: item.field,
                    label:
                        item?.meta?.options?.find((it) => it?.value === value)
                            ?.label ?? '',
                    value: value,
                }));
                filters = [...filters, ...data];
            });

        return filters;
    }, [filtersConfig, filtersState]);

    const handleClearFilter = useCallback(
        (field: string, value: string | number) => {
            const selectedFilterConfig = filtersConfig?.find(
                (filter) => filter.field === field,
            );
            if (isMultiSelect) {
                if (selectedFilterConfig) {
                    const newValues = (
                        getSelectedDropdownValue(
                            selectedFilterConfig.field,
                            isMultiSelect,
                        ) as SearchableSelectType[]
                    ).filter((filter) => filter.value !== value);
                    handleFilterChange?.(selectedFilterConfig!, newValues);
                }
            } else {
                handleFilterChange?.(selectedFilterConfig!, []);
            }
        },
        [
            filtersConfig,
            getSelectedDropdownValue,
            handleFilterChange,
            isMultiSelect,
        ],
    );

    return (
        <>
            {' '}
            {!!displayFilters.length && (
                <StyledDisplayFilter
                    displayFilters={displayFilters}
                    setFieldValue={setFieldValue}
                    clearAllFilters={() => {
                        filtersConfig?.forEach((item) => {
                            handleFilterChange?.(item, []);
                        });
                    }}
                    clearFilters={handleClearFilter}
                />
            )}
        </>
    );
};

const StyledDisplayFilter = styled(FilterDisplay)({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    flexWrap: 'wrap',
    marginLeft: '0px',
    '@media (max-width: 1439px)': {
        marginLeft: `0px`,
    },
});

export default FilterChip;
